import PropTypes from 'prop-types';
import React from 'react';
import {ArrowDirectionType, ArrowLineType} from '../../../constants/dataEnum';
import './ArrowLayer.scss';

// based on
// https://stackoverflow.com/questions/39553105/drawing-curved-svg-arrow-lines-from-div-to-div/39575674#39575674

function ArrowLayer(props) {
  const {arrowDefinitions, color, id, zIndex, isArrowFollowMouse, strokeWidth} = props;

  const drawLine = (fromX, fromY, toX, toY) => `M ${fromX} ${fromY} L ${toX} ${toY}`;

  const drawCurvedLine = (fromX, fromY, toX, toY, controlPointX1, controlPointY1, controlPointX2, controlPointY2) =>
    `M ${fromX} ${fromY} C ${controlPointX1} ${controlPointY1} ${controlPointX2} ${controlPointY2} ${toX} ${toY}`;

  const drawBowLine = (fromX, fromY, toX, toY, controlPointX, controlPointY, isRelative = false) =>
    `M ${fromX} ${fromY} ${isRelative ? 'q' : 'Q'} ${controlPointX} ${controlPointY} ${toX} ${toY}`;

  const generateControlPointOffset = (fromX, toX, curveControlPoint) => Math.floor((toX - fromX) / curveControlPoint);

  const drawCurvedLineRight = (fromX, fromY, toX, toY, curveControlPoint) => {
    const horizontalControlPointOffset = Math.abs(generateControlPointOffset(fromX, toX, curveControlPoint));
    const verControlPointOffset = Math.abs(generateControlPointOffset(fromY, toY, curveControlPoint));

    const controlPointX1 = fromX === toX ? fromX - verControlPointOffset : fromX + verControlPointOffset;
    const controlPointY1 = fromY - horizontalControlPointOffset;
    const controlPointX2 = toX - verControlPointOffset;
    const controlPointY2 = toY - horizontalControlPointOffset;

    return drawCurvedLine(fromX, fromY, toX, toY, controlPointX1, controlPointY1, controlPointX2, controlPointY2);
  };

  const drawCurvedLineLeft = (fromX, fromY, toX, toY, curveControlPoint) => {
    const horizontalControlPointOffset = Math.abs(generateControlPointOffset(fromX, toX, curveControlPoint));
    const verControlPointOffset = Math.abs(generateControlPointOffset(fromY, toY, curveControlPoint));

    const controlPointX1 = fromX - verControlPointOffset;
    const controlPointY1 = fromY - horizontalControlPointOffset;
    const controlPointX2 = fromX === toX ? toX - verControlPointOffset : toX + verControlPointOffset;
    const controlPointY2 = toY - horizontalControlPointOffset;

    return drawCurvedLine(fromX, fromY, toX, toY, controlPointX1, controlPointY1, controlPointX2, controlPointY2);
  };

  const getControlPoint = (fromX, fromY, toX, toY, offSet = 30) => {
    // source : https://stackoverflow.com/questions/49274176/how-to-create-a-curved-svg-path-between-two-points/49286885

    const mX = (toX + fromX) * 0.5;
    const mY = (toY + fromY) * 0.5;

    const theta = Math.atan2(toY - fromY, toX - fromX) - Math.PI / 2;
    const controlX = mX + offSet * Math.cos(theta);
    const controlY = mY + offSet * Math.sin(theta);
    const halfPI = Math.PI / 2;
    let headTheta = Math.atan2(toY - controlY, toX - controlX);
    let offsetX = 0;
    let offsetY = 0;
    if (isArrowFollowMouse) {
      const base = ((headTheta % halfPI) / halfPI) * 7;
      if (headTheta <= halfPI && headTheta >= halfPI * -1) {
        offsetX = Math.abs(base) - 7;
        offsetY = base * -1;
      } else {
        headTheta %= halfPI;
        offsetX = Math.abs(base);
        offsetY = (Math.abs(base) - 7) * (Math.abs(headTheta) / headTheta);
      }
    }
    return {controlX, controlY, offsetX, offsetY};
  };

  const drawBowLineRight = (fromX, fromY, toX, toY, offset, isRelative = false) => {
    const {controlX, controlY, offsetX, offsetY} = getControlPoint(fromX, fromY, toX, toY, offset, isRelative);
    return drawBowLine(fromX, fromY, toX + offsetX, toY + offsetY, controlX, controlY);
  };

  const drawBowLineLeft = (fromX, fromY, toX, toY, offset) => {
    const {controlX, controlY, offsetX, offsetY} = getControlPoint(fromX, fromY, toX, toY, offset);
    return drawBowLine(fromX, fromY, toX + offsetX, toY + offsetY, controlX, controlY);
  };

  const drawEdgedLine = (fromX, fromY, toX, toY, distanceToEdge) => {
    const edgePoint = [
      {x: fromX, y: fromY + distanceToEdge},
      {x: toX, y: fromY + distanceToEdge},
    ];

    const edgedLinePath = (
      <g key={`edge-${fromX}-${fromY}`}>
        <line x1={fromX} y1={fromY} x2={edgePoint[0].x} y2={edgePoint[0].y} />
        <line x1={edgePoint[0].x} y1={edgePoint[0].y} x2={edgePoint[1].x} y2={edgePoint[1].y} />
        <line x1={edgePoint[1].x} y1={edgePoint[1].y} x2={toX} y2={toY} />
        <path d={`M ${toX} ${toY - 5} L ${toX - 5} ${toY + 5} L ${toX + 5} ${toY + 5} z`} fill={color} />
      </g>
    );

    return edgedLinePath;
  };

  const drawArrows = () => {
    const arrows = [];

    arrowDefinitions.forEach((e, i) => {
      const {fromX, fromY, toX, toY, direction, line, curveControlPoint, offset} = e;
      let path = drawLine(fromX, fromY, toX, toY);

      if (line === ArrowLineType.EDGED) {
        const {distanceToEdge} = e;
        const paths = drawEdgedLine(fromX, fromY, toX, toY, distanceToEdge);

        arrows.push(paths);
        return;
      }

      if (line === ArrowLineType.CURVED) {
        const ccp = !curveControlPoint || curveControlPoint <= 0 ? 5 : curveControlPoint;
        path =
          direction === ArrowDirectionType.LEFT
            ? drawCurvedLineLeft(fromX, fromY, toX, toY, ccp)
            : drawCurvedLineRight(fromX, fromY, toX, toY, ccp);
      } else if (line === ArrowLineType.BOW) {
        const {isRelative, isCurvedUpwards} = e || {};

        let ofs = !offset || offset <= 0 ? 90 : offset;

        // curving is based on its direction.
        // The curve of the line is calculated counter-clockwise to a circle,
        // so if the direction is pointing to the left, then the curve is naturally downward, and vice-versa.
        // (play around on setting broadcast destination for a clear example)
        if (isCurvedUpwards) {
          ofs = direction === ArrowDirectionType.LEFT ? -ofs : ofs;
        } else {
          ofs = direction === ArrowDirectionType.LEFT ? ofs : -ofs;
        }

        path =
          direction === ArrowDirectionType.LEFT
            ? drawBowLineLeft(fromX, fromY, toX, toY, ofs)
            : drawBowLineRight(fromX, fromY, toX, toY, ofs, isRelative);
      }

      arrows.push(<path key={e.key} id={`arrow-${i}`} markerEnd={`url(#arrow-head-${id})`} d={path} />);
    });

    return (
      <g key="arrow" fill="none" stroke={color} strokeWidth={strokeWidth}>
        {arrows}
      </g>
    );
  };

  const drawArrowHead = () => (
    <defs>
      <marker
        id={`arrow-head-${id}`}
        orient="auto"
        viewBox="0 0 10 10"
        markerWidth="6"
        markerHeight="6"
        refX="3"
        refY="5"
      >
        <path d="M 0 0 L 10 5 L 0 10 z" fill={color} />
      </marker>
    </defs>
  );

  const additionalStyle = {};
  if (zIndex != null) {
    additionalStyle.zIndex = zIndex;
  }

  return (
    <svg xmlns="http://www.w3.org/2000/svg" className="arrow-layer" style={additionalStyle}>
      {drawArrowHead()}
      {drawArrows()}
    </svg>
  );
}

ArrowLayer.propTypes = {
  arrowDefinitions: PropTypes.array,
  color: PropTypes.string,
  strokeWidth: PropTypes.string,
  id: PropTypes.string,
  zIndex: PropTypes.number,
  isArrowFollowMouse: PropTypes.bool,
};

// arrowDefinitions = [{key, fromX, fromY, toX, toY, direction, line, curveControlPoint}]
// key = unique identifier
// fromX, fromY, toX, toY = int
// direction = 'left', 'right'
// line: 'straight', 'curved'
// curveControlPoint: positive integer (bigger means leaner)
ArrowLayer.defaultProps = {
  arrowDefinitions: [],
  color: '#F2994A',
  strokeWidth: '2',
  id: 'arrow-layer',
  zIndex: null,
  isArrowFollowMouse: false,
};

export default ArrowLayer;
